import React, { useState, useEffect } from 'react';
import { Shield, Star, Clock, TrendingUp, Users, CheckCircle } from 'lucide-react';

const SellTrustSignals = ({ variant = 'full' }) => {
  const [soldCount, setSoldCount] = useState(12847);

  useEffect(() => {
    const interval = setInterval(() => {
      setSoldCount(prev => prev + Math.floor(Math.random() * 3));
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  if (variant === 'compact') {
    return (
      <div className="flex flex-wrap justify-center gap-4 text-sm" data-testid="trust-signals-compact">
        <div className="flex items-center gap-1.5 text-slate-300">
          <Star className="w-4 h-4 text-yellow-500" />
          <span>4.9/5 Rating</span>
        </div>
        <div className="flex items-center gap-1.5 text-slate-300">
          <Users className="w-4 h-4 text-green-500" />
          <span>{soldCount.toLocaleString()}+ Devices Bought</span>
        </div>
        <div className="flex items-center gap-1.5 text-slate-300">
          <Clock className="w-4 h-4 text-blue-500" />
          <span>Same-Day Payment</span>
        </div>
        <div className="flex items-center gap-1.5 text-slate-300">
          <Shield className="w-4 h-4 text-purple-500" />
          <span>Price Match Available</span>
        </div>
      </div>
    );
  }

  return (
    <section className="py-12" data-testid="trust-signals-section">
      <div className="max-w-5xl mx-auto px-4">
        <h2 className="text-2xl font-bold text-white text-center mb-2">
          Trusted by Thousands Nationwide
        </h2>
        <p className="text-slate-400 text-center mb-10">
          Join {soldCount.toLocaleString()}+ satisfied sellers who chose 2A Electronics
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { icon: Users, value: `${soldCount.toLocaleString()}+`, label: 'Devices Purchased', color: 'text-green-500', bg: 'bg-green-500/10' },
            { icon: Star, value: '4.9/5', label: 'Google Rating', color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
            { icon: Clock, value: '< 24hrs', label: 'Avg. Payment Time', color: 'text-blue-500', bg: 'bg-blue-500/10' },
            { icon: TrendingUp, value: '15%', label: 'More Than Competitors', color: 'text-purple-500', bg: 'bg-purple-500/10' },
          ].map((stat, i) => (
            <div key={i} className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 text-center">
              <div className={`w-12 h-12 ${stat.bg} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
              <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
              <div className="text-slate-400 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Customer Testimonials */}
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { name: 'Sarah M.', location: 'Los Angeles, CA', text: 'Sold my iPhone 15 Pro for $150 more than Apple offered. Same-day payment via Zelle!', device: 'iPhone 15 Pro', rating: 5 },
            { name: 'James T.', location: 'New York, NY', text: 'Super easy process. Shipped my MacBook, got paid the next day. Way better than Gazelle.', device: 'MacBook Pro M2', rating: 5 },
            { name: 'Maria L.', location: 'Chicago, IL', text: 'They even bought my cracked Samsung Galaxy. Fair price and very professional service.', device: 'Galaxy S23 Ultra', rating: 5 },
          ].map((review, i) => (
            <div key={i} className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
              <div className="flex gap-0.5 mb-3">
                {[...Array(review.rating)].map((_, j) => (
                  <Star key={j} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <p className="text-slate-300 text-sm mb-4 italic">"{review.text}"</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-medium">{review.name}</p>
                  <p className="text-slate-500 text-xs">{review.location}</p>
                </div>
                <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-full">
                  Sold {review.device}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center gap-6 mt-10">
          {[
            'Price Match Available',
            'Free Shipping Both Ways',
            'Same-Day Payment',
            'Secure Data Wipe',
            'All 50 States',
          ].map((badge, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>{badge}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SellTrustSignals;
